import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function MessagesPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get("/messages/conversations");
      setConversations(res.data.conversations);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleSearch = async (query) => {
    setSearch(query);
    if (!query.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      // Search ALL users (fans + artists)
      const res = await api.get("/messages/search-users", { params: { q: query } });
      setSearchResults(res.data.users.map(u => ({
        id: u.id,
        pseudo: u.pseudo,
        avatar_url: u.avatar_url,
        type: u.role || "fan",
      })));
    } catch (err) { console.error(err); }
  };

  const startConversation = async (recipientId) => {
    try {
      const res = await api.post("/messages/conversations", { recipient_id: recipientId });
      navigate(`/messages/${res.data.conversation.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}j`;
  };

  return (
    <div className="py-6 space-y-6" data-testid="messages-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Messages</h1>
        <p className="text-sm text-muted-foreground">Tes conversations privees</p>
      </div>

      {/* Search to start new conversation */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un utilisateur..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10 bg-card border-border rounded-full text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-11"
          data-testid="messages-search"
        />
      </div>

      {/* Search results */}
      {searching && searchResults.length > 0 && (
        <div className="space-y-1" data-testid="search-results">
          <p className="text-xs text-muted-foreground px-1">Resultats</p>
          {searchResults.map((u) => (
            <button
              key={u.id}
              onClick={() => startConversation(u.id)}
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-card border border-border hover:border-[#FF6B35]/30 transition-colors text-left shadow-sm"
              data-testid={`search-result-${u.id}`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-white font-heading font-bold text-sm">
                    {(u.pseudo || "?")[0].toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <p className="font-heading font-semibold text-sm text-card-foreground">{u.pseudo}</p>
                <p className="text-[10px] text-muted-foreground">{u.type === "artist" ? "Artiste" : "Fan"}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Conversations list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-2xl h-16 animate-pulse" />
          ))}
        </div>
      ) : conversations.length === 0 && !searching ? (
        <div className="text-center py-16">
          <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Aucune conversation</p>
          <p className="text-xs text-muted-foreground mt-1">Recherche un utilisateur pour commencer a discuter</p>
        </div>
      ) : !searching && (
        <div className="space-y-1">
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/messages/${c.id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-card border border-border hover:border-[#FF6B35]/30 transition-colors text-left shadow-sm"
              data-testid={`conversation-${c.id}`}
            >
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                  {c.other_user?.avatar_url ? (
                    <img src={c.other_user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-heading font-bold">
                      {(c.other_user?.pseudo || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
                {c.unread_count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#FF6B35] rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                    {c.unread_count > 9 ? "9+" : c.unread_count}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className={`font-heading font-semibold text-sm truncate ${c.unread_count > 0 ? "text-card-foreground" : "text-card-foreground/80"}`}>
                    {c.other_user?.pseudo || "Inconnu"}
                  </p>
                  <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                    {timeAgo(c.last_message_at)}
                  </span>
                </div>
                {c.last_message && (
                  <p className={`text-xs truncate mt-0.5 ${c.unread_count > 0 ? "text-card-foreground/80 font-medium" : "text-muted-foreground"}`}>
                    {c.last_message}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
